/**
 * Temporary screen-scoped design tokens for **Settings → Document Settings**.
 *
 * Mirrors the Settings design (`Settings.dc.html`, "Document Settings" section)
 * 1:1. Every colour, size, radius, spacing and type value the screen needs
 * lives here — components must never inline a literal. Once all screens are
 * re-skinned the shared values move into `src/theme` and this file is removed.
 */

import { button, contentCard, formField, signer, switchControl } from "theme/colors";

export const documentSettingsTokens = {
  /** Two equal columns of white cards; taller cards must not stretch siblings. */
  grid: {
    gap: "20px",
  },
  /** White rounded card holding one settings group. */
  card: {
    border: contentCard.borderMuted,
    radius: "14px",
    shadow: "0 1px 3px rgba(33,21,71,.04)",
    padding: "24px 28px",
    /** Gap between header block, control block and the button row. */
    gap: "16px",
  },
  /** Card heading + supporting sentence, with the toggle pushed to the right. */
  header: {
    gap: "14px",
    textGap: "3px",
    titleColor: signer.ink,
    titleFontSize: "15px",
    titleFontWeight: 800,
    titleLetterSpacing: "-0.01em",
    descriptionColor: signer.text.meta,
    descriptionFontSize: "12.5px",
    descriptionLineHeight: 1.55,
  },
  /** Shared app toggle chrome — see `theme/colors.ts` `switchControl`. */
  toggle: {
    width: switchControl.width,
    height: switchControl.height,
    knobSize: switchControl.knobSize,
    knobTop: `${switchControl.knobInset}px`,
    knobOffsetOff: `translateX(${switchControl.knobInset}px)`,
    knobOffsetOn: `translateX(${switchControl.knobInset + switchControl.knobTravel}px)`,
    trackOff: switchControl.trackOff,
    trackOn: switchControl.trackOn,
    knobBg: switchControl.knob,
    knobShadow: switchControl.knobShadow,
    transition: switchControl.trackTransition,
    knobTransition: switchControl.knobTransition,
    /** Optically aligns the pill with the first line of the heading. */
    marginTop: "2px",
    /** Small padlock shown beside the toggle when the plan lacks the feature. */
    lockSize: 14,
    lockColor: signer.text.faint,
    lockGap: "6px",
  },
  /** "Set first reminder in [ 3 ] days" inline rows. */
  numberRow: {
    /** Vertical gap between two stacked rows. */
    stackGap: "10px",
    gap: "10px",
    fontSize: "12.5px",
    fontWeight: 600,
    labelWidth: 150,
    labelColor: signer.text.meta,
    suffixColor: signer.text.faint,
    suffixFontWeight: 500,
    /** Whole block dims while the feature is switched off. */
    disabledOpacity: 0.45,
    opacityTransition: "opacity .15s",
    errorFontSize: "10.5px",
  },
  /** Compact centred number box used by the inline rows. */
  numberField: {
    width: 64,
    height: 36,
    radius: "9px",
    border: formField.border,
    bg: formField.bg,
    color: signer.ink,
    fontSize: "13px",
    fontWeight: 600,
    paddingX: "10px",
    focusBorder: signer.orange.main,
  },
  /** Labelled "Date format" dropdown. */
  select: {
    maxWidth: 220,
    labelGap: "5px",
    labelFontSize: "11.5px",
    labelFontWeight: 600,
    labelColor: signer.ink,
    height: 40,
    radius: "9px",
    border: formField.border,
    bg: formField.bg,
    color: signer.ink,
    fontSize: "13px",
    fontWeight: 600,
    /**
     * Design inset is 12px from the border. MUI paints the outline on an
     * overlaid `fieldset` instead of insetting it, so the 1px border has to be
     * added back here to land the value text and chevron on the design.
     */
    paddingX: "13px",
    /** Right inset of the value text: 13px edge + 11px chevron + 8px gap. */
    textPaddingRight: "32px",
    focusBorder: signer.orange.main,
    iconSize: 11,
    iconColor: signer.text.faint,
    iconStrokeWidth: 2.5,
    /** Popup list rendered in the same ink as the trigger. */
    menuRadius: "10px",
    menuBorder: contentCard.borderMuted,
    menuShadow: "0 8px 24px rgba(33,21,71,.10)",
    menuPadding: "4px",
    optionHeight: 34,
    optionRadius: "7px",
    optionPaddingX: "10px",
    optionHoverBg: signer.surface.muted,
    optionSelectedBg: "#ECEAF2",
  },
  /**
   * Colours come from the global `MuiButton` `contained` override — see
   * `button`; this screen keeps the design's compact height / type step.
   */
  primaryButton: {
    ...button,
    height: 34,
    paddingX: "18px",
    fontSize: "12.5px",
    lockSize: 14,
  },
  /** Placeholder blocks shown while the configuration loads. */
  skeleton: {
    titleWidth: 180,
    titleHeight: 18,
    descriptionHeight: 13,
    descriptionWidth: "78%",
    rowLabelWidth: 150,
    rowLabelHeight: 12,
    buttonWidth: 92,
  },
} as const;

/**
 * Phone and tablet values for **Settings → Document Settings**, traced from
 * `Settings - Mobile & Tablet.dc.html`.
 *
 * The reference draws 375, 390 and 834 identically — one column of cards, each
 * closing with a full-width Update. Applied below `md`; desktop is untouched.
 */
export const documentSettingsResponsiveTokens = {
  card: { padding: "16px", gap: "14px" },
  header: { titleFontSize: "14.5px", descriptionFontSize: "12px" },
  numberRow: {
    gap: "10px",
    fontSize: "12.5px",
    /** The label takes the free space instead of a fixed column. */
    suffixWidth: "34px",
  },
  numberField: { height: 40 },
  select: { height: 44, fontSize: "13.5px" },
  primaryButton: {
    height: "44px",
    paddingX: "20px",
    radius: "9px",
    fontSize: "13px",
  },
} as const;

export default documentSettingsTokens;
